import type { ApiErrorCode } from '../types/api';

/**
 * Friendly, user-facing copy for each known backend error code. Tailored to
 * the staging-review + Sage contexts where these codes originate today.
 *
 * TODO(shared-error-codes): this map is hand-maintained alongside
 * `ApiErrorCode` in `../types/api.ts` and the C# codes emitted by
 * `GlobalExceptionMiddleware`. A missing entry silently falls through to the
 * generic default. Follow-up: codegen from a C# enum or shared constants file
 * so a backend-side code addition without a TS entry fails at build time.
 * See plan `/Users/leonvandenberg/.claude/plans/crystalline-prancing-plum.md` line 178.
 */
const FRIENDLY_MESSAGES: Record<ApiErrorCode, string> = {
  SESSION_NOT_READY_TO_COMMIT:
    "This session can't be submitted in its current state. We've refreshed it — please review the data and try again.",
  SESSION_CONCURRENCY:
    'Another submission for this session is already in progress. Please wait a moment and try again.',
  COMMIT_FAILED:
    'Something went wrong while submitting. The session is still editable — please review and try again.',
  DB_WRITE_FAILED:
    "We couldn't save your changes. The session is still editable — please review and try again, or contact support if the issue persists.",
  NO_APPROVED_ROWS:
    'No rows are approved for submission. Please approve at least one row before submitting.',
  METADATA_INCOMPLETE:
    'Some required information is missing. Please complete the farmer and farm details before submitting.',
  ROWS_MISSING_CROP:
    'One or more rows are missing a crop type or subtype. Please complete those before submitting.',
  SESSION_FILES_CHANGED:
    'The sample files for this workflow changed after this review was created, so it still shows the data from the previous files. Re-stage the workflow to read the new files.',
  ROWS_MISSING_SAMPLE_NUMBER:
    "One or more rows have no sample number, so they can't be identified. This usually means the lab file's sample number column wasn't recognised — please correct the sample numbers or re-upload the file.",
  SESSION_NOT_FOUND:
    "We couldn't find this staging session. It may have been removed — please return to the staging list and start again.",
  ROW_NOT_FOUND:
    "We couldn't find one of the rows you edited. Please refresh and try again.",
  WORKFLOW_NOT_FOUND:
    "We couldn't find the linked workflow. Please refresh and try again.",
  DUPLICATE_ACTIVE_SESSION:
    'Another active staging session already exists for this workflow. Please finish or cancel it before starting a new one.',
  WORKFLOW_WRONG_STATUS:
    "This workflow can't accept submissions in its current state. Please refresh the workflow page.",
  SESSION_WRONG_STATUS:
    "This session can't be modified in its current state. Please refresh and try again.",
  FILE_PARSE_FAILED:
    "We couldn't read that file. Please check the format and try uploading again.",
  FILE_TYPE_REQUIRED:
    "We couldn't tell from this file's columns whether it is Sap, Tissue or Soil. Pick the analysis type and upload again.",
  FILE_TYPE_MISMATCH:
    "The file name, the selected type and the file's columns disagree on the analysis type. Confirm which type to import.",
  INTERNAL_ERROR:
    'Something went wrong on our side. Please try again — if the issue persists, contact support with the trace ID shown.',
  SAGE_SUPPLIER_EXISTS:
    'A matching supplier already exists in Sage. You can link to the existing record instead.',
  SAGE_CUSTOMER_EXISTS:
    'A matching customer already exists in Sage. You can link to the existing record instead.',
  SALES_REP_EXISTS:
    'A matching Sales Representative already exists in Sage. Link it instead of creating a duplicate.',
  INACTIVE_SALES_REP:
    'That Sales Representative is inactive in Sage. Reactivate it in Sage, then refresh.',
  SAGE_VALIDATION_FAILED:
    "Sage rejected the request — some details didn't pass validation. Please check the form and try again.",
  SAGE_NOT_FOUND:
    "We couldn't find that record in Sage. It may have been removed or never linked.",
  SAGE_AUTH_FAILED:
    'The Sage connection needs to be re-authorized. Please reconnect Sage in Settings and try again.',
  SAGE_RATE_LIMIT:
    'Sage is throttling requests right now. Please wait a moment and try again.',
  SAGE_QUOTA_EXCEEDED:
    'Your Sage account has hit its usage quota. Please check your Sage subscription or try again later.',
  SAGE_API_ERROR:
    'Sage returned an unexpected error. Please try again — if the issue persists, contact support with the trace ID shown.',
  SAGE_NOT_CONFIGURED:
    'Sage is not configured on this server yet. Ask an administrator to set the Sage credentials.',
  CROP_NAME_TAKEN:
    'A crop with this name already exists — select it from the crop list instead.',
  SUBTYPE_NAME_TAKEN:
    'This crop already has a subtype with that name — select it from the list instead.',
  SUBTYPE_REQUIRED:
    'A crop needs at least one subtype. Please add a subtype name.',
  CROP_NOT_FOUND:
    "We couldn't find the selected crop. Please refresh the crop list and try again.",
  PARTNER_BATCH_FORBIDDEN:
    "You don't have access to this batch. Please check that it was shared with your organisation.",
  PARTNER_BATCH_MERGED:
    'This batch has been merged into another and is no longer available on its own. Please open the current batch instead.',
  PARTNER_BATCH_UNAVAILABLE:
    "This batch isn't available right now. Please refresh and try again, or contact support if the issue persists.",
  EXTERNAL_BOUNDARY_DENY:
    "You don't have permission to view this data. If you believe this is a mistake, please contact your administrator.",
  NO_PARTNER_ORG:
    'Your account is not linked to a partner organisation. Please contact support to set this up.',
  EMAIL_EXISTS:
    'A user with this email address already exists. Please use a different email.',
  TARGET_NOT_FOUND:
    "We couldn't find that team member. They may have been removed — please refresh and try again.",
  LAST_PARTNER_ADMIN:
    "You can't remove or demote the last partner admin. Please assign another admin first.",
  CANNOT_SELF_DEMOTE:
    "You can't change your own admin role. Please ask another partner admin to make this change.",
  CANNOT_DEACTIVATE_SELF:
    "You can't deactivate your own account. Please ask another partner admin to do this.",
  INVITE_DISABLED:
    'Inviting new team members is currently disabled for your organisation. Please contact support to enable it.',
  RATE_LIMITED:
    'Too many attempts — please wait a few minutes and try again.',
  SLOT_TAKEN:
    'That time is no longer available — it may have just been taken. Please pick another slot.',
  OTP_PROOF_EXPIRED:
    'Your verification expired — please request a new code and verify again.',
  SAMPLE_INTAKE_CONFLICT:
    'This intake entry was changed elsewhere (another tab or user). Please refresh to see its latest state.',
  INSUFFICIENT_CREDITS:
    'You have no analysis credits left. Buy analyses to submit this lab report — your uploaded file was not used.',
  BILLING_DISABLED: 'Buying analyses is not available at the moment. Please try again later.',
  BILLING_PROFILE_INCOMPLETE: 'Complete your invoice details before buying analyses.',
  PACK_NOT_FOUND: 'That pack is no longer available. Please choose another.',
  PAYMENT_AMOUNT_MISMATCH:
    'We could not verify this payment against your order, so no credits were added. Please contact support.',
  GATEWAY_ERROR: 'The payment provider is not responding right now. Please try again in a moment.',
  // Smart Sampling External self-service entitlement (§4.1).
  SUBSCRIPTION_REQUIRED:
    'A Smart Sampling subscription is needed to do this. Start a free trial or subscribe to continue.',
  TRIAL_LIMIT:
    'Your free Smart Sampling trial does not cover this. Subscribe for unlimited runs, soil and yield imports.',
  FAIR_USE_EXCEEDED:
    'You have reached this month’s fair-use limit for Smart Sampling. Your existing runs and reports stay available; new runs can start next month.',
  RUN_IN_FLIGHT:
    'You already have a Smart Sampling run in progress. Please wait for it to finish before starting another.',
  EXTERNAL_CAPACITY:
    'Smart Sampling is busy right now. Please try starting your run again in a few minutes.',
  NOT_A_SOIL_BATCH: 'This batch has no soil samples.',
  NO_SOIL_SAMPLES: 'The soil samples of this batch no longer exist.',
  // Staging owner guard (DRI-99). OWNERSHIP_INCONSISTENT's full copy is composed by getOwnershipErrorMessage.
  OWNERSHIP_INCONSISTENT:
    'Some samples are not on their farmer\'s farm. Open Commit again and resolve the group with these samples.',
  OWNERSHIP_CHANGED: 'The owners changed since you opened this; review again.',
};

const DEFAULT_FRIENDLY_MESSAGE =
  'Something went wrong. Please try again — if the issue persists, contact support.';

interface NormalizedErrorShape {
  errorCode?: ApiErrorCode | null;
  message?: string;
}

/**
 * Returns a user-safe, actionable message for a caught error.
 *
 * Preferred dispatch path: the response interceptor normalizes `ApiResponse`
 * failures into errors carrying `errorCode`. If that's present and known, we
 * return tailored copy. Otherwise we fall back to {@link getErrorMessage}.
 *
 * Additive — `getErrorMessage` retains its existing signature and behaviour.
 */
export function getFriendlyErrorMessage(err: unknown, fallback = DEFAULT_FRIENDLY_MESSAGE): string {
  if (typeof err === 'object' && err !== null && 'errorCode' in err) {
    const code = (err as NormalizedErrorShape).errorCode;
    if (code && code in FRIENDLY_MESSAGES) {
      return FRIENDLY_MESSAGES[code];
    }
  }
  return getErrorMessage(err, fallback);
}

/** Where an ownership error surfaced: the commit, an owner-group reassign, or a step-2 plot mapping. */
export type OwnershipErrorSite = 'commit' | 'reassign' | 'mapping';

const OWNERSHIP_SAMPLE_REMEDY = 'Open Commit again and resolve the group with these samples.';
const OWNERSHIP_BATCH_PAIR_REMEDY = "Fix the workflow's farmer and farm.";
const OWNERSHIP_MAPPING_COPY = "This row's farm changed; mappings were reloaded. Map the plot again.";

/** The error's code when it carries one (a NormalizedApiError from the response interceptor). */
export function getErrorCode(err: unknown): ApiErrorCode | null {
  if (typeof err === 'object' && err !== null && 'errorCode' in err) {
    return (err as NormalizedErrorShape).errorCode ?? null;
  }
  return null;
}

/** True for the two DRI-99 owner-guard codes, on which both review hooks reload the session. */
export function isOwnershipError(err: unknown): boolean {
  const code = getErrorCode(err);
  return code === 'OWNERSHIP_INCONSISTENT' || code === 'OWNERSHIP_CHANGED';
}

/**
 * The one helper both review hooks use for the staging owner guard (DRI-99 m13/m16/m17/m20): it
 * composes the server message (which lists the sample numbers) with copy keyed on errorContext.reason
 * and the call site. Any other error falls back to {@link getFriendlyErrorMessage}.
 */
export function getOwnershipErrorMessage(err: unknown, site: OwnershipErrorSite, fallback?: string): string {
  const code = getErrorCode(err);
  if (code === 'OWNERSHIP_CHANGED') return FRIENDLY_MESSAGES.OWNERSHIP_CHANGED;
  if (code !== 'OWNERSHIP_INCONSISTENT') return getFriendlyErrorMessage(err, fallback);

  const context = (err as { errorContext?: Record<string, unknown> | null }).errorContext;
  const reason = typeof context?.reason === 'string' ? context.reason : null;
  if (site === 'mapping' || reason === 'plot') return OWNERSHIP_MAPPING_COPY;

  const serverMessage = getErrorMessage(err, '').trim();
  const remedy = reason === 'batch_pair' ? OWNERSHIP_BATCH_PAIR_REMEDY
    : site === 'commit' ? OWNERSHIP_SAMPLE_REMEDY
    : '';
  return [serverMessage, remedy].filter(Boolean).join(' ') || FRIENDLY_MESSAGES.OWNERSHIP_INCONSISTENT;
}

/**
 * Safely extracts an error message from an unknown error value.
 *
 * Checks for:
 * 1. Axios-style errors with `response.data.message`
 * 2. Standard Error objects with `.message`
 * 3. Falls back to the provided default string
 */
export function getErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  // Axios-style error: err.response?.data?.message
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err
  ) {
    const response = (err as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }

  // Standard Error object
  if (err instanceof Error && err.message) {
    return err.message;
  }

  // Plain object with message property
  if (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof (err as { message?: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message;
  }

  return fallback;
}
